import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { t } from "i18next";
import { ReactElement, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  ContractDto,
  ContractRoleType,
  PersonDto,
  useCreateContractParticipantMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { RadioFormSelector } from "../../../../common/input/Radio/RadioFormSelector";
import { PersonSelectorForm } from "../../../../common/persons/PersonSelectorForm";

interface Props {
  data: ContractDto;
  onClose: () => void;
}

interface FormValues {
  person: PersonDto | null;
  roleType: ContractRoleType;
  shouldNotifyByEmail: boolean;
}

export const AddInternalParticipantModal = ({ data, onClose }: Props) => {
  const toast = useToast();
  const displayer = useApiError();
  const authState = useLoggedInWithOrgContextState();

  const [createParticipant] = useCreateContractParticipantMutation();

  const methods = useForm<FormValues>({
    defaultValues: { roleType: ContractRoleType.Editor, shouldNotifyByEmail: true },
  });
  const [isLoading, setIsLoading] = useState(false);

  const create = async (values: FormValues) => {
    if (!values.person) return;
    setIsLoading(true);
    try {
      await createParticipant({
        contractId: data.id,
        createContractParticipantRequest: {
          organizationId: authState.selectedOrg.id,
          personId: values.person.id,
          roleType: values.roleType,
          shouldNotifyByEmail: values.shouldNotifyByEmail,
        },
      }).unwrap();
      toast({ title: t("Participant added!"), status: "success" });
      onClose();
    } catch (e) {
      setIsLoading(false);
      displayer.trigger(e);
    }
  };

  const options: {
    label: string;
    description: ReactElement;
    value: ContractRoleType;
  }[] = [
    {
      label: t("Editor"),
      description: (
        <Text fontSize="sm" color="smMuted">
          {t("Can see and edit everything")}.
        </Text>
      ),
      value: ContractRoleType.Editor,
    },
    {
      label: t("Viewer"),
      description: (
        <Text fontSize="sm" color="smMuted">
          {t("Can see everything, but not edit anything")}.
        </Text>
      ),
      value: ContractRoleType.Viewer,
    },
  ];

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Share contract internally")}</ModalHeader>
        <ModalCloseButton />
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(create)}>
            <ModalBody>
              <PersonSelectorForm<FormValues>
                organizationIds={[authState.selectedOrg.id]}
                label={t("Share with person in your organization")}
                control={methods.control}
                name="person"
                placeholderText={`${t("Select a person from your organization")}...`}
                errorMessage={undefined}
                helperText={`${t("Select a specific person to share the contract with")}.`}
              />
              <Box pt="6">
                <RadioFormSelector options={options} label={t("Role")} control={methods.control} name={"roleType"} />
              </Box>
              <FormControl pt="6">
                <Controller<FormValues>
                  name={"shouldNotifyByEmail"}
                  control={methods.control}
                  render={({ field: { onChange, value } }) => (
                    <Checkbox isChecked={!!value} name="shouldNotifyByEmail" onChange={onChange}>
                      {t("Send email notification to participant(s)")}
                    </Checkbox>
                  )}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isLoading}>
                {t("Cancel")}
              </Button>
              <Button
                type="submit"
                variant="solid"
                colorScheme="teal"
                isLoading={isLoading}
                isDisabled={!methods.watch("person")}
              >
                {t("Add")}
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};
